import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { ArrowRight, ArrowLeft, Check, MessageCircle, Clock, Phone, ShieldCheck, Shield, Star, Users } from 'lucide-react';
import axios from 'axios';
import { getTrafficAttribution } from '../components/Analytics';
import CallbackModal from '../components/CallbackModal';
import LiveVisitorCount from '../components/LiveVisitorCount';

const API = '/api';

const LoanRequest3eChance = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    vehicleType: '',
    budgetRange: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [emailError, setEmailError] = useState(false);
  const [showChatWidget, setShowChatWidget] = useState(false);
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [chatPhone, setChatPhone] = useState('');
  const [chatMessage, setChatMessage] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatSent, setChatSent] = useState(false);
  const [chatFormData, setChatFormData] = useState({
    firstName: '', lastName: '', email: '', vehicleType: '', budgetRange: ''
  });

  useEffect(() => {
    document.title = 'Prêt Auto 3e Chance | Credit Auto Reset';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Obtenez un prêt auto 3e chance au Québec. Faillite, mauvais crédit, refus? 99% d\'approbation, financement rapide et sans obligation!');
  }, []);

  const handleStartChat = async () => {
    if (!chatFormData.firstName.trim() || !chatFormData.lastName.trim() ||
        !chatFormData.email.trim() || !chatPhone.trim() ||
        !chatFormData.vehicleType || !chatFormData.budgetRange) {
      toast({ title: 'Veuillez remplir tous les champs', variant: 'destructive' });
      return;
    }
    let formattedPhone = chatPhone.replace(/\D/g, '');
    if (formattedPhone.length === 10) formattedPhone = '+1' + formattedPhone;
    else if (!formattedPhone.startsWith('+')) formattedPhone = '+' + formattedPhone;

    const vehicleLabel = chatVehicleTypes.find(v => v.value === chatFormData.vehicleType)?.labelFr || chatFormData.vehicleType;
    const budgetLabel = chatBudgetOptions.find(b => b.value === chatFormData.budgetRange)?.labelFr || chatFormData.budgetRange;
    const fullMessage = `New Live Chat Request (3e Chance):\nName: ${chatFormData.firstName} ${chatFormData.lastName}\nEmail: ${chatFormData.email}\nPhone: ${formattedPhone}\nVehicle: ${vehicleLabel}\nBudget: ${budgetLabel}\nMessage: ${chatMessage || 'Customer wants to chat live'}`;

    setChatSending(true);
    try {
      await axios.post(`${API}/start-chat`, {
        customer_phone: formattedPhone,
        customer_name: `${chatFormData.firstName} ${chatFormData.lastName}`,
        customer_email: chatFormData.email,
        vehicle_type: chatFormData.vehicleType,
        budget_range: chatFormData.budgetRange,
        message: fullMessage,
        language
      });
      setChatSent(true);
      toast({ title: 'Message envoyé! Vérifiez votre téléphone pour notre réponse.' });
    } catch {
      toast({ title: 'Echec de l\'envoi. Veuillez reessayer.', variant: 'destructive' });
    }
    setChatSending(false);
  };

  const resetChatWidget = () => {
    setShowChatWidget(false);
    setChatSent(false);
    setChatPhone('');
    setChatMessage('');
    setChatFormData({ firstName: '', lastName: '', email: '', vehicleType: '', budgetRange: '' });
  };

  const chatVehicleTypes = [
    { value: 'sedan', labelEn: 'Auto', labelFr: 'Auto' },
    { value: 'suv', labelEn: 'SUV', labelFr: 'VUS' },
    { value: 'truck', labelEn: 'Truck', labelFr: 'Camion' }
  ];
  const chatBudgetOptions = [
    { value: 'under-350', labelEn: 'Less than $350/mo', labelFr: 'Moins de 350$/mois' },
    { value: '351-475', labelEn: '$351-$475/mo', labelFr: '351$-475$/mois' },
    { value: '476-600', labelEn: '$476-$600/mo', labelFr: '476$-600$/mois' },
    { value: '601-750', labelEn: '$601-$750/mo', labelFr: '601$-750$/mois' },
    { value: '751-900', labelEn: '$751-$900/mo', labelFr: '751$-900$/mois' },
    { value: '901-plus', labelEn: '$901+/mo', labelFr: '901$+/mois' }
  ];

  const vehicleTypes = [
    { value: 'sedan', labelEn: 'Auto', labelFr: 'Auto', image: '/images/vehicle-sedan.avif' },
    { value: 'suv', labelEn: 'SUV', labelFr: 'VUS', image: '/images/vehicle-suv.png' },
    { value: 'truck', labelEn: 'Truck', labelFr: 'Camion', image: '/images/vehicle-truck.webp' }
  ];
  const budgetOptions = [
    { value: 'under-350', labelEn: 'Less than $350/month', labelFr: 'Moins de 350 $/mois' },
    { value: '351-475', labelEn: '$351 - $475/month', labelFr: '351 $ - 475 $/mois' },
    { value: '476-600', labelEn: '$476 - $600/month', labelFr: '476 $ - 600 $/mois' },
    { value: '601-750', labelEn: '$601 - $750/month', labelFr: '601 $ - 750 $/mois' },
    { value: '751-900', labelEn: '$751 - $900/month', labelFr: '751 $ - 900 $/mois' },
    { value: '901-plus', labelEn: '$901+ /month', labelFr: '901 $ et plus/mois' }
  ];

  const handleVehicleSelect = (type) => {
    setFormData({ ...formData, vehicleType: type });
    setTimeout(() => setCurrentStep(2), 300);
  };
  const handleBudgetSelect = (budget) => {
    setFormData({ ...formData, budgetRange: budget });
    setTimeout(() => setCurrentStep(3), 300);
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'email') setEmailError(value.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value));
  };
  const handleNext = () => {
    if (currentStep === 1 && !formData.vehicleType) { toast({ title: 'Veuillez choisir un type de véhicule', variant: 'destructive' }); return; }
    if (currentStep === 2 && !formData.budgetRange) { toast({ title: 'Veuillez choisir une fourchette budgétaire', variant: 'destructive' }); return; }
    setCurrentStep(currentStep + 1);
  };
  const handleBack = () => setCurrentStep(currentStep - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setEmailError(true);
      toast({ title: 'Veuillez entrer une adresse courriel valide', variant: 'destructive' });
      return;
    }
    setIsSubmitting(true);
    try {
      const attribution = getTrafficAttribution();
      await axios.post(`${API}/loan-requests`, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        employmentStatus: 'employed',
        annualIncome: '0',
        vehicleType: formData.vehicleType,
        budgetRange: formData.budgetRange,
        message: '',
        source: 'Google Ads - 3e Chance',
        traffic_source: attribution?.source || 'google_ads',
        traffic_medium: attribution?.medium || 'cpc',
        campaign: attribution?.campaign || null,
        keyword: attribution?.keyword || null,
        gclid: attribution?.gclid || null
      });
      navigate('/merci');
    } catch {
      toast({ title: 'Erreur lors de la soumission. Veuillez réessayer.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-16 relative overflow-hidden" data-testid="loan-3e-chance-page">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-white/90 z-10"></div>
        <img src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1600" alt="" className="w-full h-full object-cover opacity-30" />
      </div>

      <div className="relative z-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* SEO Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#1e7d55] to-[#1abc9c] text-white px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-4 shadow-lg">
            <ShieldCheck className="w-4 h-4" />
            <span>3e CHANCE AU CRÉDIT AUTO</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-5 leading-tight" data-testid="3e-chance-h1">
            Prêt Auto <span className="text-[#1a5c3b]">3e Chance</span> au Crédit
          </h1>
          <div className="max-w-3xl mx-auto space-y-4 text-gray-700 text-base md:text-lg leading-relaxed mb-6">
            <p>
              Vous avez été refusé plusieurs fois ? Notre programme de <strong>financement auto 3e chance</strong> est conçu pour les situations
              les plus difficiles. Même après une faillite, une proposition ou un crédit sévèrement endommagé.
            </p>
            <p>
              Avec notre <strong>prêt auto 3e chance</strong>, nous travaillons avec des prêteurs spécialisés qui comprennent votre situation
              et offrent des solutions réelles pour vous remettre au volant.
            </p>
            <p className="font-semibold text-[#1a5c3b]">
              Faites votre demande dès maintenant — aucun cas n'est refusé.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mb-6">
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm">
              <ShieldCheck className="w-5 h-5 text-[#1a5c3b]" />
              <span className="text-sm font-semibold text-gray-700">Aucun cas refusé</span>
            </div>
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm">
              <Star className="w-5 h-5 text-[#1a5c3b]" />
              <span className="text-sm font-semibold text-gray-700">0$ comptant requis</span>
            </div>
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm">
              <Users className="w-5 h-5 text-[#1a5c3b]" />
              <span className="text-sm font-semibold text-gray-700">Réponse en 24h</span>
            </div>
          </div>
          <div className="flex justify-center mt-3">
            <LiveVisitorCount variant="inline" />
          </div>
        </div>

        {/* Two Column: Form + Chat */}
        <div className="grid lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-3">
            <Card className="shadow-2xl border-2 border-gray-200" data-testid="3e-chance-form-card">
              <div className="bg-[#1a5c3b] p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <ArrowRight className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-bold text-white text-lg">Demander un Prêt Auto 3e Chance</h2>
                  <p className="text-xs text-green-200">Tous les dossiers sont acceptés</p>
                </div>
              </div>
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center justify-center mb-8">
                  <div className="flex items-center space-x-3">
                    {[1, 2, 3].map((step) => (
                      <React.Fragment key={step}>
                        <div className="flex items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                            currentStep === step ? 'bg-[#1a5c3b] text-white ring-2 ring-green-200' :
                            currentStep > step ? 'bg-[#1e7d55] text-white' : 'bg-gray-200 text-gray-600'
                          }`}>
                            {currentStep > step ? <Check className="w-4 h-4" /> : step}
                          </div>
                          <span className={`ml-2 text-xs font-bold hidden sm:inline ${currentStep === step ? 'text-[#1a5c3b]' : 'text-gray-500'}`}>
                            {step === 1 && 'Véhicule'}
                            {step === 2 && 'Budget'}
                            {step === 3 && 'Contact'}
                          </span>
                        </div>
                        {step < 3 && <div className={`w-8 h-0.5 ${currentStep > step ? 'bg-[#1e7d55]' : 'bg-gray-200'}`}></div>}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {currentStep === 1 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 text-center mb-4">Choisissez un type de véhicule</h3>
                    <div className="grid grid-cols-3 gap-3">
                      {vehicleTypes.map((vehicle) => (
                        <button key={vehicle.value} onClick={() => handleVehicleSelect(vehicle.value)} data-testid={`3e-vehicle-${vehicle.value}`}
                          className={`relative overflow-hidden rounded-xl border-2 transition-all duration-300 ${
                            formData.vehicleType === vehicle.value ? 'border-[#1a5c3b] shadow-lg ring-2 ring-green-200' : 'border-gray-200 hover:border-green-400'
                          }`}>
                          <div className="bg-gray-50 h-28 flex items-end justify-center overflow-hidden pb-1">
                            <img src={vehicle.image} alt={vehicle.labelFr} className={vehicle.value === 'sedan' ? 'w-32 h-20 object-contain' : 'w-36 h-24 object-contain'} />
                          </div>
                          <div className="bg-white p-2 border-t text-center">
                            <span className="text-gray-700 font-medium text-sm">{vehicle.labelFr}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 text-center mb-4">Quel est votre budget ?</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {budgetOptions.map((budget) => (
                        <button key={budget.value} onClick={() => handleBudgetSelect(budget.value)} data-testid={`3e-budget-${budget.value}`}
                          className={`p-3 rounded-lg border-2 font-medium text-sm text-left transition-all ${
                            formData.budgetRange === budget.value ? 'border-[#1a5c3b] bg-[#1a5c3b]/10 text-[#1a5c3b]' : 'border-gray-200 hover:border-green-400 text-gray-700 bg-white'
                          }`}>
                          {budget.labelFr}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {currentStep === 3 && (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <h3 className="text-xl font-semibold text-gray-800 text-center mb-8">Vos coordonnées</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="firstName" className="text-sm font-semibold text-gray-700">Prénom *</Label>
                        <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} required className="h-12 text-base" data-testid="3e-input-firstname" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName" className="text-sm font-semibold text-gray-700">Nom *</Label>
                        <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} required className="h-12 text-base" data-testid="3e-input-lastname" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-semibold text-gray-700">Courriel *</Label>
                        <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required
                          className={`h-12 text-base ${emailError ? 'border-red-500 border-2' : ''}`} data-testid="3e-input-email" />
                        {emailError && <p className="text-red-500 text-xs mt-1">Veuillez entrer une adresse courriel valide</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-sm font-semibold text-gray-700">Téléphone *</Label>
                        <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} required className="h-12 text-base" data-testid="3e-input-phone" />
                      </div>
                    </div>
                  </form>
                )}

                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                  <Button type="button" onClick={handleBack} disabled={currentStep === 1} variant="outline" size="sm">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Retour
                  </Button>
                  {currentStep < 3 ? (
                    <Button type="button" onClick={handleNext} size="sm" className="bg-[#1a5c3b] hover:bg-[#165432] text-white">
                      Continuer <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  ) : (
                    <Button type="submit" onClick={handleSubmit} disabled={isSubmitting} size="sm" className="bg-[#1a5c3b] hover:bg-[#165432] text-white" data-testid="3e-submit-btn">
                      {isSubmitting ? 'Envoi...' : 'Vérifier mon admissibilité gratuitement'} <Check className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>

                {/* Trust Badges */}
                <div className="flex flex-wrap justify-center gap-2 mt-4 pt-4 border-t border-gray-100" data-testid="loan-trust-badges">
                  <div className="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-full px-3 py-1.5 text-xs font-semibold text-green-700">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    99% approuvé
                  </div>
                  <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-full px-3 py-1.5 text-xs font-semibold text-blue-700">
                    <Shield className="w-3.5 h-3.5" />
                    Aucun impact sur votre crédit
                  </div>
                  <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5 text-xs font-semibold text-amber-700">
                    <Clock className="w-3.5 h-3.5" />
                    Réponse en 24h
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Callback */}
          <div className="lg:col-span-2 lg:mt-16">
            <Card className="border-2 border-[#1a5c3b] bg-gradient-to-br from-[#1a5c3b]/5 to-white shadow-xl mt-4">
              <div className="bg-[#1a5c3b] p-3 flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <Phone className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-sm">Demander un rappel</h3>
                  <p className="text-xs text-green-200">On vous appelle!</p>
                </div>
              </div>
              <CardContent className="p-4">
                <p className="text-gray-600 text-sm mb-4 text-center">Trop occupé? Planifiez un moment pour qu'on vous rappelle!</p>
                <button onClick={() => setShowCallbackModal(true)} className="w-full inline-flex items-center justify-center gap-2 bg-[#1a5c3b] hover:bg-[#165432] text-white px-4 py-2.5 rounded-full font-semibold text-sm shadow-lg transition-all duration-300" data-testid="3e-callback-button">
                  <Phone className="w-4 h-4" /> Demander un rappel
                </button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* SEO Content Section */}
        <div className="mt-12 max-w-4xl mx-auto">
          <Card className="bg-white/95 shadow-lg border border-gray-200">
            <CardContent className="p-6 md:p-8 space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Le financement auto 3e chance : votre dernier recours, notre spécialité</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Le <strong>prêt auto 3e chance</strong> s'adresse aux personnes qui ont déjà été refusées par les institutions financières traditionnelles
                  et même par certains prêteurs de 2e chance. Chez <strong>Crédit Auto Reset</strong>, nous ne refusons personne.
                </p>
                <p>
                  Notre programme de <strong>financement auto 3e chance</strong> fonctionne grâce à un réseau exclusif de prêteurs privés qui
                  évaluent votre capacité de paiement actuelle, et non seulement votre historique de crédit.
                </p>
                <h3 className="text-xl font-semibold text-[#1a5c3b] pt-2">Comment fonctionne la 3e chance au crédit auto ?</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Aucune enquête de crédit</strong> — Nous ne regardons pas votre passé</li>
                  <li><strong>Approbation garantie</strong> — Tous les dossiers sont acceptés</li>
                  <li><strong>0$ comptant</strong> — Aucun dépôt requis pour commencer</li>
                  <li><strong>Véhicules fiables</strong> — Inspectés et garantis</li>
                  <li><strong>Rebâtissez votre crédit</strong> — Chaque paiement améliore votre cote</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <CallbackModal isOpen={showCallbackModal} onClose={() => setShowCallbackModal(false)} />
    </div>
  );
};

export default LoanRequest3eChance;
